import { FC } from "react";

import { CategoryIconKey } from "../config/categories";

/**
 * Çizgisel ikon seti. Kategori ikonları inline SVG: react-icons/fi setinde erlen yok.
 */

interface IconProps {
  size?: number;
  className?: string;
  strokeWidth?: number;
}

const base = (size: number, strokeWidth: number) => ({
  width: size,
  height: size,
  viewBox: "0 0 24 24",
  fill: "none",
  stroke: "currentColor",
  strokeWidth,
  strokeLinecap: "round" as const,
  strokeLinejoin: "round" as const,
  "aria-hidden": true,
  focusable: false,
});

export const IconDocument: FC<IconProps> = ({ size = 21, className, strokeWidth = 1.9 }) => (
  <svg {...base(size, strokeWidth)} className={className}>
    <path d="M14 3H7a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8z" />
    <path d="M14 3v5h5" />
    <path d="M9 13h6M9 17h4" />
  </svg>
);

export const IconShield: FC<IconProps> = ({ size = 21, className, strokeWidth = 1.9 }) => (
  <svg {...base(size, strokeWidth)} className={className}>
    <path d="M12 3l7 3v5.5c0 4.4-2.9 8.4-7 9.5-4.1-1.1-7-5.1-7-9.5V6z" />
    <path d="M9.2 12.1l2 2 3.6-3.8" />
  </svg>
);

export const IconPerson: FC<IconProps> = ({ size = 21, className, strokeWidth = 1.9 }) => (
  <svg {...base(size, strokeWidth)} className={className}>
    <circle cx="12" cy="8" r="3.6" />
    <path d="M4.8 20c0-3.6 3.2-5.8 7.2-5.8s7.2 2.2 7.2 5.8" />
  </svg>
);

export const IconDashboard: FC<IconProps> = ({ size = 21, className, strokeWidth = 1.9 }) => (
  <svg {...base(size, strokeWidth)} className={className}>
    <rect x="3.5" y="3.5" width="17" height="17" rx="2.2" />
    <path d="M3.5 9.2h17M9.2 9.2v11.3" />
  </svg>
);

export const IconFlask: FC<IconProps> = ({ size = 21, className, strokeWidth = 1.9 }) => (
  <svg {...base(size, strokeWidth)} className={className}>
    <path d="M10 3h4M10.4 3v6.2L5.2 18a2 2 0 0 0 1.7 3h10.2a2 2 0 0 0 1.7-3l-5.2-8.8V3" />
    <path d="M7.6 14.4h8.8" />
  </svg>
);

export const IconMobile: FC<IconProps> = ({ size = 21, className, strokeWidth = 1.9 }) => (
  <svg {...base(size, strokeWidth)} className={className}>
    <rect x="7" y="2.5" width="10" height="19" rx="2.4" />
    <path d="M11 18.4h2" />
  </svg>
);

const CATEGORY_ICONS: Record<CategoryIconKey, FC<IconProps>> = {
  document: IconDocument,
  shield: IconShield,
  person: IconPerson,
  dashboard: IconDashboard,
  flask: IconFlask,
  mobile: IconMobile,
};

export const CategoryIcon: FC<IconProps & { icon: CategoryIconKey }> = ({ icon, ...rest }) => {
  const Component = CATEGORY_ICONS[icon] || IconMobile;
  return <Component {...rest} />;
};

export const IconChevronDown: FC<IconProps> = ({ size = 14, className, strokeWidth = 2 }) => (
  <svg {...base(size, strokeWidth)} className={className}>
    <path d="M6 9l6 6 6-6" />
  </svg>
);

export const IconSearch: FC<IconProps> = ({ size = 19, className, strokeWidth = 2 }) => (
  <svg {...base(size, strokeWidth)} className={className}>
    <circle cx="11" cy="11" r="7" />
    <path d="M20 20l-3.6-3.6" />
  </svg>
);

export const IconArrowRight: FC<IconProps> = ({ size = 19, className, strokeWidth = 2 }) => (
  <svg {...base(size, strokeWidth)} className={className}>
    <path d="M4 12h15M13 6l6 6-6 6" />
  </svg>
);

export const IconMenu: FC<IconProps> = ({ size = 20, className, strokeWidth = 2 }) => (
  <svg {...base(size, strokeWidth)} className={className}>
    <path d="M4 7h16M4 12h16M4 17h16" />
  </svg>
);

export const IconClose: FC<IconProps> = ({ size = 18, className, strokeWidth = 2 }) => (
  <svg {...base(size, strokeWidth)} className={className}>
    <path d="M6 6l12 12M18 6L6 18" />
  </svg>
);

export const IconPencil: FC<IconProps> = ({ size = 16, className, strokeWidth = 2 }) => (
  <svg {...base(size, strokeWidth)} className={className}>
    <path d="M4 20h4L19 9a2.1 2.1 0 0 0-3-3L5 17z" />
    <path d="M14.5 6.5l3 3" />
  </svg>
);

export const IconTrash: FC<IconProps> = ({ size = 16, className, strokeWidth = 2 }) => (
  <svg {...base(size, strokeWidth)} className={className}>
    <path d="M4 7h16M9.5 7V5h5v2M6.5 7l.8 12.2A1.8 1.8 0 0 0 9.1 21h5.8a1.8 1.8 0 0 0 1.8-1.8L17.5 7" />
  </svg>
);

export const IconUserPlus: FC<IconProps> = ({ size = 16, className, strokeWidth = 2 }) => (
  <svg {...base(size, strokeWidth)} className={className}>
    <circle cx="10" cy="8" r="3.4" />
    <path d="M3.8 20c0-3.4 2.8-5.4 6.2-5.4 1.2 0 2.3.2 3.2.7" />
    <path d="M17 14.5v5M14.5 17h5" />
  </svg>
);

export const IconLogin: FC<IconProps> = ({ size = 16, className, strokeWidth = 2 }) => (
  <svg {...base(size, strokeWidth)} className={className}>
    <path d="M14 3h4.2A1.8 1.8 0 0 1 20 4.8v14.4a1.8 1.8 0 0 1-1.8 1.8H14" />
    <path d="M4 12h11M11 8l4 4-4 4" />
  </svg>
);

export const IconGridView: FC<IconProps> = ({ size = 17, className, strokeWidth = 2 }) => (
  <svg {...base(size, strokeWidth)} className={className}>
    <rect x="3.5" y="3.5" width="7" height="7" rx="1.6" />
    <rect x="13.5" y="3.5" width="7" height="7" rx="1.6" />
    <rect x="3.5" y="13.5" width="7" height="7" rx="1.6" />
    <rect x="13.5" y="13.5" width="7" height="7" rx="1.6" />
  </svg>
);

export const IconListView: FC<IconProps> = ({ size = 17, className, strokeWidth = 2 }) => (
  <svg {...base(size, strokeWidth)} className={className}>
    <path d="M4 6.5h16M4 12h16M4 17.5h16" />
  </svg>
);

export const IconPlus: FC<IconProps> = ({ size = 17, className, strokeWidth = 2 }) => (
  <svg {...base(size, strokeWidth)} className={className}>
    <path d="M12 5v14M5 12h14" />
  </svg>
);

/** Kategori yönetimini açan buton; Feather "sliders". */
export const IconSliders: FC<IconProps> = ({ size = 17, className, strokeWidth = 2 }) => (
  <svg {...base(size, strokeWidth)} className={className}>
    <path d="M4 21v-7M4 10V3M12 21v-9M12 8V3M20 21v-5M20 12V3" />
    <path d="M1 14h6M9 8h6M17 16h6" />
  </svg>
);

/** `direction` yoksa iki ok da çizilir, varsa yalnızca aktif yön. */
export const IconSort: FC<IconProps & { direction?: "asc" | "desc" | null }> = ({
  size = 12,
  className,
  strokeWidth = 2.2,
  direction = null,
}) => (
  <svg {...base(size, strokeWidth)} className={className}>
    {direction !== "desc" && <path d="M7 10l5-5 5 5" />}
    {direction !== "asc" && <path d="M7 14l5 5 5-5" />}
  </svg>
);

export const IconExport: FC<IconProps> = ({ size = 16, className, strokeWidth = 1.9 }) => (
  <svg {...base(size, strokeWidth)} className={className}>
    <path d="M14 3H7a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8z" />
    <path d="M14 3v5h5" />
    <path d="M9 12h6M9 16h6M12 12v4" />
  </svg>
);

export const IconSun: FC<IconProps> = ({ size = 16, className, strokeWidth = 2 }) => (
  <svg {...base(size, strokeWidth)} className={className}>
    <circle cx="12" cy="12" r="4" />
    <path d="M12 2v2M12 20v2M4.9 4.9l1.4 1.4M17.7 17.7l1.4 1.4M2 12h2M20 12h2M4.9 19.1l1.4-1.4M17.7 6.3l1.4-1.4" />
  </svg>
);

export const IconMoon: FC<IconProps> = ({ size = 16, className, strokeWidth = 2 }) => (
  <svg {...base(size, strokeWidth)} className={className}>
    <path d="M21 12.8A9 9 0 1 1 11.2 3a7 7 0 0 0 9.8 9.8z" />
  </svg>
);

/** "Sistem" seçeneği: cihazın kendi tercihi. */
export const IconDisplay: FC<IconProps> = ({ size = 16, className, strokeWidth = 2 }) => (
  <svg {...base(size, strokeWidth)} className={className}>
    <rect x="2.5" y="4" width="19" height="12.5" rx="2" />
    <path d="M8.5 20.5h7M12 16.5v4" />
  </svg>
);
